import DefaultEmailSection from "./DefaultEmailSection"
import GiftEmailSection from "./GiftEmailSection"
import LotteryEmailSection from "./LotteryEmailSection"

import {
	FragmentType,
	getFragmentData,
	graphql,
} from "@/public/gql/__generated__"

// メール登録セクションの中間 fragment (fragment collocation).
// 自分の分岐 (どのセクションを出すか) に使う incentive の type / lotteryMultiplier を宣言し、
// LOTTERY 用の表示フィールドは子 fragment (...lotteryEmailSection) を nest spread して子に任せる.
// lotteryMultiplier は表示用ではなく「LOTTERY でも口数が無ければ通常フォームに落とす」分岐のために持つ.
const emailSectionFragment = graphql(/* GraphQL */ `
	fragment emailSection on CanceledReservationV2 {
		incentive {
			type
			lotteryMultiplier
		}
		...lotteryEmailSection
	}
`)

type Props = {
	// キャンセル情報を取得できなかった場合は null (通常のメール登録フォームに落とす)
	canceledReservation: FragmentType<typeof emailSectionFragment> | null
	email: string
	setEmail: (email: string) => void
	isEmailSubmitted: boolean
	isSubmittingEmail: boolean
	handleEmailSubmit: (e: React.FormEvent) => void
}

// LOTTERY 用セクション (応募条件 + メール登録フォーム) を出すかどうか.
// LOTTERY の時だけ完了メッセージより上に置くため、親 (完了画面) も配置の判定に使う.
// サーバは LOTTERY なら必ず口数 (lotteryMultiplier) を返す前提だが、
// 万一 null なら通常のメール登録フォームに落とす.
export const isLotteryEmailSection = (
	canceledReservation: FragmentType<typeof emailSectionFragment> | null
): boolean => {
	if (canceledReservation === null) {
		return false
	}
	const incentive = getFragmentData(
		emailSectionFragment,
		canceledReservation
	).incentive
	return incentive?.type === "LOTTERY" && incentive.lotteryMultiplier !== null
}

// LOTTERY 時は「メール登録で当選口数 N 倍」訴求、GIFT 時は強化警告付き Gift 用、
// GIFT_CANDIDATE (予算ありギフト) 時は同じ Gift 用に「予算に達し次第終了」を足したもの、
// それ以外は通常リマインダー用に分岐.
const EmailSection = ({ canceledReservation, ...rest }: Props) => {
	const summary =
		canceledReservation !== null
			? getFragmentData(emailSectionFragment, canceledReservation)
			: null
	const incentive = summary?.incentive ?? null

	if (summary !== null && isLotteryEmailSection(canceledReservation)) {
		return <LotteryEmailSection canceledReservation={summary} {...rest} />
	}
	if (incentive?.type === "GIFT" || incentive?.type === "GIFT_CANDIDATE") {
		return (
			<GiftEmailSection
				{...rest}
				showsBudgetNote={incentive.type === "GIFT_CANDIDATE"}
			/>
		)
	}
	return <DefaultEmailSection {...rest} />
}

export default EmailSection
